<template>
  <div class="token-explorer" v-if="mounted">
    <!-- Discovery tabs -->
    <div class="d-flex flex-wrap align-items-center gap-2 mb-3">
      <button
        v-for="tab in tabs"
        :key="tab.key"
        class="btn btn-sm"
        :class="activeTab === tab.key ? 'tab-active' : 'btn-outline-secondary'"
        @click="switchTab(tab.key)"
      >{{ tab.label }}</button>

      <span class="border-start mx-1 align-self-stretch"></span>

      <!-- Category pills -->
      <a
        v-for="(cat, slug) in categories"
        :key="slug"
        :href="'/tokens/category/' + slug"
        class="btn btn-sm"
        :class="activeCategory === slug ? 'tab-active' : 'btn-outline-secondary'"
        @click.prevent="switchCategory(slug)"
      >{{ cat.label }}</a>
    </div>

    <!-- Market cap segment pills + sort + result count -->
    <div class="d-flex flex-wrap justify-content-between align-items-center mb-2">
      <div class="d-flex flex-wrap gap-1">
        <button
          v-for="seg in segments"
          :key="seg.key"
          class="btn btn-xs"
          :class="activeSegment === seg.key ? 'btn-primary' : 'btn-outline-secondary'"
          @click="switchSegment(seg.key)"
        >{{ seg.label }}</button>
      </div>
      <div class="d-flex align-items-center gap-2">
        <small class="text-muted">{{ shortNumber(total) }} tokens</small>
        <select v-model="currentSort" @change="fetchData" class="form-select form-select-sm" style="width: auto;">
          <option value="volume">Volume</option>
          <option value="change">Price Change</option>
          <option value="trades">Trades</option>
          <option value="market_cap">Market Cap</option>
        </select>
      </div>
    </div>

    <!-- Loading overlay -->
    <div v-if="loading" class="text-center py-4">
      <div class="spinner-border spinner-border-sm text-muted" role="status"></div>
      <span class="text-muted ms-2 small">Loading...</span>
    </div>

    <!-- Token table -->
    <div class="card" v-show="!loading">
      <div class="table-responsive" style="overflow: visible;">
        <table class="table table-hover mb-0 token-table">
          <thead>
            <tr>
              <th>Token</th>
              <th class="text-end">Price</th>
              <th class="text-center">24h</th>
              <th class="text-end">1h</th>
              <th class="text-end">24h</th>
              <th class="text-end">Volume</th>
              <th class="text-end">MCap</th>
              <th class="text-end">Liquidity</th>
              <th class="text-end">Net Flow</th>
              <template v-if="debug">
                <th class="text-end">Trades</th>
                <th class="text-end">Traders</th>
                <th class="text-end">Hrs</th>
                <th class="text-end">Activity</th>
                <th class="text-end">T/Trdr</th>
                <th class="text-end">MC:Liq</th>
              </template>
            </tr>
          </thead>
          <tbody>
            <tr v-for="token in tokens" :key="token.mint" class="token-row" @click="navigateToToken(token.mint)">
              <!-- Token: logo + symbol + name -->
              <td class="token-cell">
                <div class="d-flex align-items-center gap-2">
                  <img
                    v-if="token.logoURI"
                    :src="token.logoURI"
                    :alt="token.symbol"
                    class="token-logo"
                    loading="lazy"
                  />
                  <div v-else class="token-logo-placeholder">
                    {{ (token.symbol || '?')[0].toUpperCase() }}
                  </div>
                  <div>
                    <div class="fw-bold token-symbol">{{ token.symbol }}</div>
                    <div class="token-name">{{ truncate(token.name, 18) }}</div>
                  </div>
                </div>
              </td>

              <!-- Price -->
              <td class="text-end mono-num">
                ${{ formatPrice(token.price_usd) }}
              </td>

              <!-- Sparkline -->
              <td class="text-center" style="padding: 4px 6px;">
                <sparkline-chart
                  :data="tokenSparkline(token).prices"
                  :width="100"
                  :height="28"
                />
              </td>

              <!-- 1h Change (computed from sparkline) -->
              <td class="text-end" :class="change1hClass(token)">
                {{ formatChange(change1h(token)) }}
              </td>

              <!-- 24h Change -->
              <td class="text-end" :class="token.change_24h_percent >= 0 ? 'text-success' : 'text-danger'">
                {{ formatChange(token.change_24h_percent) }}
              </td>

              <!-- Volume -->
              <td class="text-end">{{ shortNumber(token.volume_usd_24h, '$') }}</td>

              <!-- Market Cap -->
              <td class="text-end">
                <span v-if="token.market_cap_usd > 0">{{ shortNumber(token.market_cap_usd, '$') }}</span>
                <span v-else class="text-muted">-</span>
              </td>

              <!-- Liquidity -->
              <td class="text-end">{{ shortNumber(token.liquidity_usd || 0, '$') }}</td>

              <!-- Net Flow (hourly buy/sell volume over 24h) -->
              <td style="padding: 0 0 0 8px; width: 118px;">
                <net-flow-chart
                  :data="tokenSparkline(token).net_flow"
                  :buy-data="tokenSparkline(token).buy_vol"
                  :sell-data="tokenSparkline(token).sell_vol"
                  :total-net="token.net_flow_24h || 0"
                  :height="38"
                />
              </td>

              <!-- Debug columns -->
              <template v-if="debug">
                <td class="text-end">{{ shortNumber(token.trade_count || 0) }}</td>
                <td class="text-end">{{ shortNumber(token.traders_unique || 0) }}</td>
                <td class="text-end">{{ token.active_hours || 0 }}/24</td>
                <td class="text-end" :class="activityRatio(token) >= 0.6 ? 'text-success' : activityRatio(token) >= 0.3 ? 'text-warning' : 'text-danger'">
                  {{ activityRatio(token).toFixed(2) }}
                </td>
                <td class="text-end" :class="tradesPerTrader(token) <= 20 ? 'text-success' : tradesPerTrader(token) <= 50 ? 'text-warning' : 'text-danger'">
                  {{ tradesPerTrader(token).toFixed(1) }}
                </td>
                <td class="text-end" :class="mcapLiqRatio(token) !== null && mcapLiqRatio(token) <= 10 ? 'text-success' : mcapLiqRatio(token) !== null && mcapLiqRatio(token) <= 50 ? 'text-warning' : 'text-danger'">
                  {{ mcapLiqRatio(token) !== null ? mcapLiqRatio(token).toFixed(1) + 'x' : '-' }}
                </td>
              </template>
            </tr>

            <tr v-if="!tokens.length && !loading">
              <td :colspan="debug ? 15 : 9" class="text-center py-4 text-muted">No tokens found matching your criteria.</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <!-- Pagination -->
    <div v-if="totalPages > 1" class="d-flex justify-content-center mt-3">
      <nav>
        <ul class="pagination pagination-sm mb-0">
          <li class="page-item" :class="{ disabled: currentPage <= 1 }">
            <a class="page-link" href="#" @click.prevent="goToPage(currentPage - 1)">Prev</a>
          </li>
          <li
            v-for="p in visiblePages"
            :key="p"
            class="page-item"
            :class="{ active: p === currentPage }"
          >
            <a class="page-link" href="#" @click.prevent="goToPage(p)">{{ p }}</a>
          </li>
          <li class="page-item" :class="{ disabled: currentPage >= totalPages }">
            <a class="page-link" href="#" @click.prevent="goToPage(currentPage + 1)">Next</a>
          </li>
        </ul>
      </nav>
    </div>
  </div>

  <!-- SSR fallback (visible until Vue mounts) -->
  <div v-else>
    <slot name="ssr-fallback"></slot>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted } from 'vue';
import axios from 'axios';

const props = defineProps({
  initialData: { type: Object, required: true },
  categories: { type: Object, default: () => ({}) },
  initialFilterGroup: { type: String, default: 'top' },
  initialSort: { type: String, default: 'volume' },
  initialSegment: { type: String, default: 'all' },
  initialCategory: { type: String, default: '' },
  debug: { type: Boolean, default: false },
});

// State
const mounted = ref(false);
const loading = ref(false);
const tokens = ref(props.initialData.tokens || []);
const sparklines = ref({});
const total = ref(props.initialData.total || 0);
const currentPage = ref(props.initialData.page || 1);
const perPage = ref(props.initialData.per_page || 100);
const currentSort = ref(props.initialSort);
const activeTab = ref(props.initialFilterGroup);
const activeSegment = ref(props.initialSegment);
const activeCategory = ref(props.initialCategory);

let priceInterval = null;

// Discovery tabs
const tabs = [
  { key: 'top', label: 'Top Tokens' },
  { key: 'gainers', label: 'Gainers' },
  { key: 'losers', label: 'Losers' },
  { key: 'new', label: 'New' },
  { key: 'volume', label: 'Volume Leaders' },
];

// Market cap segments
const segments = [
  { key: 'all', label: 'All' },
  { key: 'majors', label: '>$100M' },
  { key: 'midcaps', label: '$10-100M' },
  { key: 'smallcaps', label: '$1-10M' },
  { key: 'microcaps', label: '<$1M' },
];

// Computed
const totalPages = computed(() => Math.ceil(total.value / perPage.value));

const visiblePages = computed(() => {
  const pages = [];
  const tp = totalPages.value;
  const cp = currentPage.value;
  const start = Math.max(1, cp - 3);
  const end = Math.min(tp, cp + 3);
  for (let i = start; i <= end; i++) pages.push(i);
  return pages;
});

// Formatters
const formatPrice = (price) => {
  if (!price || price === 0) return '0.00';
  if (price >= 1) return price.toFixed(2);
  if (price >= 0.01) return price.toFixed(4);
  if (price >= 0.000001) return price.toPrecision(3);
  return price.toExponential(2);
};

const shortNumber = (value, prefix = '') => {
  if (value === null || value === undefined) return prefix + '0';
  const abs = Math.abs(value);
  const sign = value < 0 ? '-' : '';
  if (abs >= 1e12) return sign + prefix + (abs / 1e12).toFixed(1) + 'T';
  if (abs >= 1e9) return sign + prefix + (abs / 1e9).toFixed(1) + 'B';
  if (abs >= 1e6) return sign + prefix + (abs / 1e6).toFixed(1) + 'M';
  if (abs >= 1e3) return sign + prefix + (abs / 1e3).toFixed(1) + 'K';
  return sign + prefix + Math.round(abs);
};

const formatChange = (val) => {
  if (val === null || val === undefined || isNaN(val)) return '-';
  const sign = val >= 0 ? '+' : '';
  return sign + val.toFixed(2) + '%';
};

const truncate = (str, len) => {
  if (!str) return '';
  return str.length > len ? str.slice(0, len) + '...' : str;
};

// Extract sparkline data (handles {prices, net_flow, buy_vol, sell_vol} or legacy flat array)
const tokenSparkline = (token) => {
  const data = sparklines.value[token.mint];
  if (!data) return { prices: [], net_flow: [], buy_vol: [], sell_vol: [] };
  if (Array.isArray(data)) return { prices: data, net_flow: [], buy_vol: [], sell_vol: [] };
  return { prices: data.prices || [], net_flow: data.net_flow || [], buy_vol: data.buy_vol || [], sell_vol: data.sell_vol || [] };
};

// Compute 1h change from sparkline data
const change1h = (token) => {
  const prices = tokenSparkline(token).prices;
  if (!prices || prices.length < 2) return null;
  const prev = prices[prices.length - 2];
  const curr = prices[prices.length - 1];
  if (!prev || prev === 0) return null;
  return ((curr - prev) / prev) * 100;
};

const change1hClass = (token) => {
  const val = change1h(token);
  if (val === null) return 'text-muted';
  return val >= 0 ? 'text-success' : 'text-danger';
};

// Debug column helpers
const activityRatio = (token) => (token.active_hours || 0) / Math.max(1, token.token_age_hours || 24);
const tradesPerTrader = (token) => (token.traders_unique || 0) > 0 ? (token.trade_count || 0) / token.traders_unique : 0;
const mcapLiqRatio = (token) => {
  const mcap = token.market_cap_usd || 0;
  const liq = token.liquidity_usd || 0;
  return (liq > 0 && mcap > 0) ? mcap / liq : null;
};

const buyVolPct = (token) => {
  const buy = token.buy_volume_24h || 0;
  const sell = token.sell_volume_24h || 0;
  const total = buy + sell;
  return total > 0 ? (buy / total) * 100 : 50;
};

// Async data fetches (non-blocking, table renders immediately without these)
const fetchAsyncData = () => {
  const mints = tokens.value.map(t => t.mint).join(',');
  if (!mints) return;

  // Sparklines and liquidity fetched in parallel
  axios.get('/api/tokens/sparklines', { params: { mints } })
    .then(({ data }) => { sparklines.value = data || {}; })
    .catch(() => {});

  axios.get('/api/tokens/liquidity', { params: { mints } })
    .then(({ data }) => {
      // Merge liquidity into tokens reactively
      tokens.value = tokens.value.map(token => ({
        ...token,
        liquidity_usd: data[token.mint] ?? token.liquidity_usd ?? 0,
      }));
    })
    .catch(() => {});
};

// Data fetching
const fetchData = async () => {
  loading.value = true;
  try {
    const params = {
      page: currentPage.value,
      sort: currentSort.value,
      filter_group: activeTab.value,
    };
    if (activeSegment.value !== 'all') params.segment = activeSegment.value;
    if (activeCategory.value) params.category = activeCategory.value;

    const { data } = await axios.get('/api/tokens/list', { params });
    tokens.value = data.tokens || [];
    total.value = data.total || 0;
    currentPage.value = data.page || 1;
    if (data.sort) currentSort.value = data.sort;
  } catch (error) {
    console.error('Error fetching token list:', error);
  } finally {
    loading.value = false;
  }
  // Fetch sparklines + liquidity in parallel after table renders
  fetchAsyncData();
};

const switchTab = (tabKey) => {
  activeTab.value = tabKey;
  activeCategory.value = '';
  activeSegment.value = 'all';
  currentPage.value = 1;
  fetchData();
};

const switchCategory = (slug) => {
  activeCategory.value = slug;
  activeTab.value = '';
  activeSegment.value = 'all';
  currentPage.value = 1;
  fetchData();
};

const switchSegment = (segKey) => {
  activeSegment.value = segKey;
  currentPage.value = 1;
  fetchData();
};

const navigateToToken = (mint) => {
  window.location.href = '/tokens/' + mint;
};

const goToPage = (page) => {
  if (page < 1 || page > totalPages.value) return;
  currentPage.value = page;
  fetchData();
  window.scrollTo({ top: 0, behavior: 'smooth' });
};

// Price refresh (every 15s)
const refreshPrices = async () => {
  const mints = tokens.value.map(t => t.mint).join(',');
  if (!mints) return;
  try {
    const { data } = await axios.get('/api/tokens/prices', { params: { mints } });
    tokens.value = tokens.value.map(token => {
      const update = data[token.mint];
      if (update && update.price_usd !== undefined) {
        return { ...token, price_usd: update.price_usd };
      }
      return token;
    });
  } catch (e) {
    // Silently fail — prices will refresh next interval
  }
};

onMounted(() => {
  mounted.value = true;
  fetchAsyncData(); // Non-blocking — table renders immediately, sparklines + liquidity fill in
  priceInterval = setInterval(refreshPrices, 15000);
});

onUnmounted(() => {
  if (priceInterval) clearInterval(priceInterval);
});
</script>

<style scoped>
.token-table thead th {
  padding: 0.4rem 0.5rem;
  font-size: 0.6875rem;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  white-space: nowrap;
  border-bottom: 2px solid rgba(139, 92, 246, 0.15);
}
.token-table tbody td {
  padding: 0.35rem 0.5rem;
  font-size: 0.8125rem;
  vertical-align: middle;
}
.token-row {
  cursor: pointer;
  transition: background-color 0.15s ease;
}
.token-row:hover {
  background-color: rgba(139, 92, 246, 0.06);
}
/* Dark mode: ensure hover text stays visible */
:global(.dark-mode) .token-row:hover,
:global(.dark-mode) .token-row:hover td {
  color: inherit;
}
.token-cell { white-space: nowrap; }
.token-logo { width: 24px; height: 24px; border-radius: 50%; }
.token-logo-placeholder {
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background: #f0f0f0;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.6875rem;
  color: #999;
}
.token-symbol { font-size: 0.8125rem; }
.token-name { font-size: 0.6875rem; opacity: 0.5; }
.mono-num { font-family: 'SF Mono', Monaco, 'Cascadia Code', monospace; }

/* Buy/sell ratio bar */
.buy-sell-bar {
  display: flex;
  width: 36px;
  height: 4px;
  border-radius: 2px;
  overflow: hidden;
  background: var(--compass-danger, #ef4444);
}
.buy-sell-bar .buy {
  background: var(--compass-success, #22c55e);
  height: 100%;
}

/* Tab pills */
.tab-active {
  background: linear-gradient(135deg, #8b5cf6, #ec4899) !important;
  color: white !important;
  border-color: transparent !important;
}

.btn-xs {
  padding: 0.15rem 0.5rem;
  font-size: 0.6875rem;
}

</style>
